/** Five hundred: red flag, session stopped.
 *
 * What the portal shows when a screen throws while rendering, in place of the
 * blank page React leaves behind. The header above it is still alive, so the
 * way out is a click; "Try again" reloads, because the state that produced
 * the throw is the state a re-render would be handed back.
 *
 * The field of red is the only saturated surface in the portal, and it is
 * allowed to be: Nocturne keeps semantic colour to small marks precisely so
 * that the one time something has actually stopped, the page can say so at a
 * glance. Its colours are in `tokens.css` with every other colour.
 *
 * The words differ from the Worker's 500 in one place. That page can promise
 * the incident was logged, because `onError` logged it; nothing records a
 * crash in somebody's browser, so this one does not say so, and prints the
 * error itself where the Worker prints a request id.
 */

import { FlagPage } from '@/components/ui/FlagPage';

function RaceControl() {
  return (
    <div className="race-control">
      <div className="race-control-sweep" />
      <div className="race-control-top">
        <span>Race control</span>
        <span className="race-control-live">● Live</span>
      </div>
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div className="race-control-kicker">All cars</div>
        <div style={{ fontSize: 40, lineHeight: 1, letterSpacing: '-0.02em' }}>Return to pits</div>
        <div style={{ fontSize: 13, opacity: 0.8, marginTop: 6 }}>
          Timing suspended · lap not counted
        </div>
      </div>
    </div>
  );
}

export interface CrashedProps {
  error: unknown;
  /** When it was caught, ISO. */
  at: string;
}

export function Crashed({ error, at }: CrashedProps) {
  const what =
    error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? 'unknown error');

  return (
    <FlagPage
      status={500}
      swatch="flag-swatch-red"
      kicker="Red flag · session stopped"
      title="Red flag. Something broke on our side of the pit wall."
      wide
      actions={
        <>
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
            Try again
          </button>
          <a className="btn btn-secondary" href="/v1/health">
            Service status
          </a>
        </>
      }
      diag={
        <>
          incident {at} · {what.slice(0, 200)}
        </>
      }
      art={<RaceControl />}
    >
      This screen hit an error it couldn't recover from. Your laps are safe — nothing was lost. Try
      again in a moment.
    </FlagPage>
  );
}

export default Crashed;
